import React, { useEffect, useCallback, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Typography, Grid } from '@material-ui/core'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Button, useConfirm } from '@refera/ui-web'
import {
  getServiceList,
  getServiceById,
  updateService,
  deleteService,
  postService,
  getProblemList,
} from '_/modules/service-orders/actions'
import Loading from '_components/loading'
import { useToast } from '_/hooks/use-toast'
import { useDialog } from '_/hooks/use-dialog'
import DynamicTreeView from '_/components/treeview'
import * as ModalComponent from '_components/modal/generic-modal'
import Autocomplete from '@mui/material/Autocomplete'

import useStyles from './styles'
import { ServiceForm } from './service-form'
import { TextField } from '@mui/material'
import ConfirmRemoveDialog from '_/components/dialogs/ConfirmRemoveDialog'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import useRolePermission from '_/hooks/use-role-permission'
import { ROUTES } from '_/utils/constants'

const ServiceScreen = () => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()
  const { checkIfUserDoesNotPermission } = useRolePermission()

  const [isDeleting, setIsDeleting] = useState(false)
  const [service, setService] = useState()
  const [serviceSelectId, setServiceSelectId] = useState(null)
  const [serviceList, setServiceList] = useState()
  const [problemsList, setProblemsList] = useState([])
  const [problem, setProblem] = useState(null)
  const [dialogInfo, setDialogInfo] = useState()
  const { isConfirmed } = useConfirm()

  const [modalOpen, setModalOpen] = useState(false)

  const methods = useForm({
    defaultValues: {
      ...service,
      problems: [],
      categories: [],
    },
    mode: 'onChange',
  })

  const {
    reset,
    setValue,
    watch,
    control,
    formState: { isDirty },
    handleSubmit,
  } = methods

  const data = watch()
  const problemsWatch = watch('problems')

  const message = {
    title: 'Você tem certeza que deseja executar esta ação?',
    error:
      'Você não pode excluir esta opção porque ela tem itens filhos ou já foi utilizada. Tente desativá-la.',
    duplicationError: 'Conteúdo já existente.',
    success: 'Ação executada com sucesso.',
  }

  const normalizeServiceFormData = useCallback(serviceData => {
    const normalizedUnid =
      typeof serviceData?.unid === 'object'
        ? serviceData?.unid?.id ?? serviceData?.unid?.value ?? ''
        : serviceData?.unid ?? ''

    return {
      ...serviceData,
      unid: normalizedUnid,
    }
  }, [])

  const handleSelection = useCallback(
    e => {
      dispatch(getServiceById(e?.id)).then(res => {
        const normalizedService = res.count ? {} : normalizeServiceFormData(res)

        setService(() => normalizedService)
        reset(normalizedService)
        setServiceSelectId(normalizedService.id)
      })
    },
    [dispatch, reset, normalizeServiceFormData]
  )

  const handleCreate = () => {
    if (checkIfUserDoesNotPermission(PERMISSIONS.SERVICES, PERMISSIONS_ACTIONS.ADD)) {
      return
    }

    setService({})

    reset({
      name: '',
      isActive: false,
      allowsNoAttachments: false,
      allowsTheDistance: false,
      additionalLabor: '0.00',
      additionalPrice: '0.00',
      allowsSelection: false,
      diagnosisDesc: null,
      externalId: null,
      idealLabor: '0.00',
      idealMaterial: '0.00',
      idealPrice: '0.00',
      maxLabor: '0.00',
      maxMaterial: '0.00',
      maxPrice: '0.00',
      materialLink: '',
      laborTypeId: null,
      unid: null,
      executionTime: '',
      problems: [],
      categories: [],
    })

    setServiceSelectId(null)
  }

  const handleCancel = useCallback(() => {
    setService(null)
    setServiceSelectId(null)
  }, [])

  const handleCancelRequest = useCallback(() => {
    if (isDirty) {
      showDialog({
        type: 'warning',
        subject: 'Cancelar alterações?',
        description: 'Você tem certeza que deseja fechar a janela sem salvar o registro?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          handleCancel()
          closeDialog()
        },
        onCancel: closeDialog,
      })
    } else {
      handleCancel()
    }
  }, [isDirty, handleCancel, showDialog, closeDialog])

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await dispatch(deleteService(serviceSelectId))
      showToast({
        message: message.success,
        type: 'success',
      })
      dispatch(getServiceList()).then(res => {
        setServiceList(res)
        setServiceSelectId(null)
        setService(null)
      })
    } catch (error) {
      showToast({
        message: error?.error_message || message.error,
        type: 'error',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const openDeleteModal = () => {
    if (checkIfUserDoesNotPermission(PERMISSIONS.SERVICES, PERMISSIONS_ACTIONS.DELETE)) {
      return
    }

    showDialog({
      type: 'warning',
      subject: message.title,
      labelApprove: 'Sim',
      labelCancel: 'Não',
      onApprove: () => {
        closeDialog()
        handleDelete()
      },
      onCancel: closeDialog,
    })
  }

  const handleCreateService = useCallback(
    async payload => {
      if (checkIfUserDoesNotPermission(PERMISSIONS.SERVICES, PERMISSIONS_ACTIONS.ADD)) {
        return
      }

      const payloadToSend = {
        ...payload,
        problems: payload?.problems?.map(item => item.id),
      }

      await Promise.resolve(
        dispatch(
          serviceSelectId
            ? updateService(serviceSelectId, payloadToSend)
            : postService({ ...payloadToSend, parent: service?.id })
        )
      )
        .catch(error =>
          showToast({
            message: error?.error_message ?? message.error,
            type: 'error',
          })
        )
        .then(() => {
          showToast({
            message: message.success,
            type: 'success',
          })

          dispatch(getServiceList()).then(res => {
            setServiceList(res)
          })
        })
    },
    [handleCreate, showToast, service]
  )

  const handleProblemSelect = useCallback(
    problemItem => {
      const findIfSelectedProblemAlreadyInList = problemsWatch?.some(
        p => p?.id === problemItem?.id && p?.name === problemItem?.name
      )

      if (problemsList && !findIfSelectedProblemAlreadyInList) {
        const updatedProblems = [...problemsWatch, { id: problemItem?.id, name: problemItem?.name }]
        setValue('problems', updatedProblems)
      }
      setProblem(null)
      setModalOpen(false)
    },
    [problemsWatch, problemsList, setValue, reset]
  )

  const handleDeleteProblem = useCallback(
    async problemData => {
      setDialogInfo({ message: 'Você tem certeza que deseja executar esta ação?' })
      const confirmed = await isConfirmed()

      if (problemsWatch && confirmed) {
        const updatedProblems = problemsWatch?.filter(
          currentProblem => currentProblem.id !== problemData.id
        )
        setValue('problems', updatedProblems)
      }
    },
    [problemsWatch]
  )

  const getOptionProblemsLabel = useCallback(optionProblems => {
    return optionProblems?.name
  }, [])

  const getOptionProblemsSelected = useCallback((optionProblems, currentValue) => {
    return optionProblems.id === currentValue
  }, [])

  const getOptionProblemsDisabled = useCallback(
    optionProblems => {
      return data?.services && data?.services.some(item => item.id === optionProblems.id)
    },
    [data]
  )

  useEffect(() => {
    dispatch(getServiceList()).then(res => setServiceList(res))
    dispatch(
      getProblemList({
        active: true,
      })
    ).then(res => setProblemsList(res))
  }, [])

  const formattedServiceList = useMemo(() => {
    if (!serviceList) return serviceList

    const sortedList = [...serviceList].sort((a, b) => {
      const idA = a.externalId
      const idB = b.externalId

      if (idA === idB) return 0
      if (idA === null || idA === undefined) return 1
      if (idB === null || idB === undefined) return -1

      if (typeof idA === 'number' && typeof idB === 'number') {
        return idA - idB
      }

      return String(idA).localeCompare(String(idB), undefined, { numeric: true })
    })

    return sortedList.map(item => ({
      ...item,
      name: item.externalId ? `${item.externalId} - ${item.name}` : item.name,
    }))
  }, [serviceList])

  const categoryForms = useMemo(() => {
    if (service) {
      return (
        <ServiceForm
          service={service}
          serviceSelectId={serviceSelectId}
          handleCreate={handleCreateService}
          setModalOpen={setModalOpen}
          handleDeleteProblem={handleDeleteProblem}
          onDelete={openDeleteModal}
          isDeleting={isDeleting}
        />
      )
    }

    return (
      <Grid className={styles.selectWrapper}>
        <Typography className={styles.selectTitle}>Selecione um serviço.</Typography>
      </Grid>
    )
  }, [
    service,
    handleCreateService,
    handleDeleteProblem,
    serviceSelectId,
    isDeleting,
    openDeleteModal,
  ])

  const handleOpenTypes = useCallback(() => {
    window.open(ROUTES.SERVICES_TYPE_LIST, '_blank', 'noopener,noreferrer')
  }, [])

  if (!serviceList) {
    return <Loading />
  }

  return (
    <FormProvider {...methods}>
      <Grid className={styles.container}>
        <Grid className={styles.header}>
          <Typography variant="h4" component="h1" className={styles.title}>
            Serviços
          </Typography>
          <Grid className={styles.buttonWrapper}>
            {service && (
              <>
                <Button onClick={handleCancelRequest} variant="secondary" color="red">
                  Cancelar
                </Button>
                <Button onClick={handleSubmit(handleCreateService)}>Salvar</Button>
              </>
            )}
            {!service && <Button onClick={() => handleOpenTypes()}>Tipos de MO</Button>}
            <Button onClick={() => handleCreate()}>Novo</Button>
          </Grid>
        </Grid>

        <Grid className={styles.main}>
          <Grid className={styles.treeWrapper}>
            <DynamicTreeView data={formattedServiceList} handleClick={handleSelection} />
          </Grid>
          <Grid className={styles.displayWrapper}>{categoryForms}</Grid>
        </Grid>
      </Grid>

      <ModalComponent.Root
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        keepMounted
      >
        <ModalComponent.TitleModal title="Selecione um problema" />
        <ModalComponent.Content className={styles.modalContent}>
          <Grid className={{ width: '600px' }}>
            <Controller
              name="problems"
              shouldUnregister={false}
              control={control}
              defaultValue=""
              render={() => {
                return (
                  <>
                    <Typography className={styles.serviceLabel}>Problemas</Typography>
                    <Autocomplete
                      disablePortal
                      className={styles.autocomplete}
                      multiple={false}
                      options={problemsList}
                      defaultValue=""
                      getOptionSelected={getOptionProblemsSelected}
                      getOptionLabel={getOptionProblemsLabel}
                      getOptionDisabled={getOptionProblemsDisabled}
                      noOptionsText="Nenhum problema encontrado."
                      value={problem}
                      onChange={(event, newValue) => {
                        setProblem(newValue)
                      }}
                      ListboxProps={{
                        style: {
                          fontSize: '14px',
                        },
                      }}
                      renderInput={params => <TextField {...params} />}
                    />
                  </>
                )
              }}
            />
          </Grid>
        </ModalComponent.Content>
        <ModalComponent.Actions>
          <ModalComponent.ButtonRed onClick={() => setModalOpen(false)}>
            Cancelar
          </ModalComponent.ButtonRed>
          <ModalComponent.ButtonFullBlue
            disabled={!problem}
            onClick={() => handleProblemSelect(problem)}
          >
            Salvar
          </ModalComponent.ButtonFullBlue>
        </ModalComponent.Actions>
      </ModalComponent.Root>

      {dialogInfo?.message && <ConfirmRemoveDialog message={dialogInfo.message} />}
    </FormProvider>
  )
}

export default ServiceScreen
